import type {
  NavigationFailure,
  RouteLocation,
  RouteLocationNamedRaw,
  RouteLocationNormalizedGeneric,
  Router,
} from "vue-router";

import Resolvable from "@/utils/async/Resolvable";
import { createSyncQueue } from "@/utils/async/syncQueue";

import type { SyncRouter } from "./types";

export function addSyncNav(router: Router): SyncRouter {
  // queue to synchronize programmatic changes to the URL
  const queue = createSyncQueue<void | NavigationFailure | undefined>();

  // navigatingTo and navigating promise are set for the duration of a navigation action
  let navigatingTo: RouteLocation | undefined;
  let navigating = new Resolvable<void>();
  navigating.resolve();

  router.beforeEach(startNavigating);
  router.afterEach(endNavigating);
  router.onError(endNavigating);

  const syncRouter = router as SyncRouter;
  syncRouter.syncPush = syncPush;
  syncRouter.syncReplace = syncReplace;
  return syncRouter;

  function startNavigating(to: RouteLocationNormalizedGeneric) {
    navigatingTo = to;
    // no new navigating promise while the old one is still pending
    // there might be syncPush/syncReplace waiting on the old promise, so it must be resolved
    if (!navigating.pending) {
      navigating = new Resolvable<void>();
    }
  }

  function endNavigating() {
    navigatingTo = undefined;
    navigating.resolve();
  }

  function syncPush(routeFunc: () => RouteLocationNamedRaw) {
    return queue.add(() => navigating.promise.then(() => router.push(routeFunc())), {
      waitingOn: navigatingTo?.fullPath,
      target: routeFunc(),
    });
  }

  function syncReplace(routeFunc: () => RouteLocationNamedRaw) {
    return queue.add(() => navigating.promise.then(() => router.replace(routeFunc())), {
      waitingOn: navigatingTo?.fullPath,
      target: routeFunc(),
    });
  }
}
